import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import {
  Avatar, Box, CircularProgress, ClickAwayListener, Paper, Popper, Stack,
  TextField, Typography,
} from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import { useQuery } from '@tanstack/react-query'
import type { ChatMention, MentionSearchResult, MentionType } from '@cmms/types'

import { api } from '../../lib/api'

/**
 * Triggers that fire the picker. `@` is people-only; `#` searches every
 * non-user entity type. Keeps the UX clean — typing `#` for a person or
 * `@` for a work order would feel off in muscle memory.
 */
const TRIGGER_TYPES: Record<string, MentionType[]> = {
  '@': ['user'],
  '#': ['work_order', 'asset', 'location', 'vendor', 'part', 'checklist'],
}

const TYPE_META: Record<MentionType, { icon: React.ElementType; color: string; label: string }> = {
  user:       { icon: PersonOutlineIcon,         color: '#0D9488', label: 'Person' },
  work_order: { icon: BuildOutlinedIcon,         color: '#3B82F6', label: 'Work Order' },
  asset:      { icon: Inventory2OutlinedIcon,    color: '#6366F1', label: 'Asset' },
  location:   { icon: LocationOnOutlinedIcon,    color: '#0EA5E9', label: 'Location' },
  vendor:     { icon: StorefrontOutlinedIcon,    color: '#EA580C', label: 'Vendor' },
  part:       { icon: CategoryOutlinedIcon,      color: '#D97706', label: 'Part' },
  checklist:  { icon: ChecklistOutlinedIcon,     color: '#8B5CF6', label: 'Checklist' },
}

/**
 * Picker state — describes the active trigger we're matching against.
 * Null when there's no open picker. `start` is the index of the trigger
 * char so we know where to splice when the user picks a result.
 */
interface PickerState {
  trigger: '@' | '#'
  query: string
  start: number
  end: number
}

interface Props {
  value: string
  onChange: (text: string, mentions: ChatMention[]) => void
  mentions: ChatMention[]
  onSubmit?: () => void
  placeholder?: string
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | null>
  disabled?: boolean
  multiline?: boolean
  maxRows?: number
  sx?: any
}

/**
 * Drop-in replacement for the chat composer's TextField. Detects `@` /
 * `#` triggers, opens an autocomplete popper, and maintains the parent's
 * `mentions` array in sync with the text (drops mentions whose labels
 * have been deleted).
 */
export function MentionComposer({
  value, onChange, mentions, onSubmit, placeholder, inputRef,
  disabled, multiline = true, maxRows = 4, sx,
}: Props) {
  const ownInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const ref = inputRef ?? ownInputRef
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [picker, setPicker] = useState<PickerState | null>(null)
  const [activeIdx, setActiveIdx] = useState(0)

  // Watch the caret position to detect trigger contexts on every change.
  function handleChange(next: string) {
    const input = ref.current
    const caret = input?.selectionStart ?? next.length
    detectTrigger(next, caret)
    // Drop any stale mentions whose label no longer appears in the text —
    // covers the case where the user backspaces through a mention chip.
    const stillThere = mentions.filter((m) =>
      next.includes(`${m.type === 'user' ? '@' : '#'}${m.label}`)
    )
    onChange(next, stillThere)
  }

  function detectTrigger(text: string, caret: number) {
    // Walk back from the caret looking for the most recent `@` or `#`
    // that's either at the start of the text or preceded by whitespace.
    // Stop if we hit whitespace before a trigger (no active picker).
    for (let i = caret - 1; i >= 0; i--) {
      const ch = text[i]
      if (ch === ' ' || ch === '\n' || ch === '\t') return setPicker(null)
      if (ch === '@' || ch === '#') {
        const before = i === 0 ? ' ' : text[i - 1]
        if (before !== ' ' && before !== '\n' && before !== '\t' && i !== 0) {
          // `@` mid-word (like an email) — don't trigger.
          return setPicker(null)
        }
        const query = text.slice(i + 1, caret)
        // Trigger only when the user is actively typing the query —
        // bail out if they've typed any whitespace into it, which means
        // they've moved past the mention.
        if (/\s/.test(query)) return setPicker(null)
        setPicker({ trigger: ch, query, start: i, end: caret })
        setActiveIdx(0)
        return
      }
    }
    setPicker(null)
  }

  // Re-detect on caret moves so arrow-key navigation between mentions
  // toggles the picker the way users expect.
  function handleSelect() {
    const input = ref.current
    if (!input) return
    detectTrigger(value, input.selectionStart ?? 0)
  }

  // Picker autocomplete query — debounced via React Query's staleTime
  // so consecutive keystrokes share a single fetch.
  const types = picker ? TRIGGER_TYPES[picker.trigger] : []
  const searchQ = useQuery({
    queryKey: ['mention-search', picker?.trigger, picker?.query],
    queryFn: () => api.chat.searchMentions(picker!.query, types),
    enabled: !!picker && picker.query.length >= 0,
    staleTime: 30_000,
  })
  const results: MentionSearchResult[] = Array.isArray(searchQ.data) ? searchQ.data : []

  // Keep activeIdx in range as results change underneath us.
  useEffect(() => {
    if (activeIdx >= results.length) setActiveIdx(0)
  }, [results.length, activeIdx])

  function pickResult(r: MentionSearchResult) {
    if (!picker) return
    const prefix = picker.trigger
    const insert = `${prefix}${r.label}`
    const next = value.slice(0, picker.start) + insert + ' ' + value.slice(picker.end)
    const newMentions: ChatMention[] = [
      ...mentions.filter((m) => m.id !== r.id || m.type !== r.type),
      { type: r.type, id: r.id, label: r.label },
    ]
    onChange(next, newMentions)
    setPicker(null)
    // Restore focus + caret position right after the inserted token + space.
    requestAnimationFrame(() => {
      const input = ref.current
      if (!input) return
      input.focus()
      const pos = picker.start + insert.length + 1
      input.setSelectionRange(pos, pos)
    })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (picker && results.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % results.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i - 1 + results.length) % results.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickResult(results[activeIdx]!); return }
      if (e.key === 'Escape') { e.preventDefault(); setPicker(null); return }
    }
    // No picker → standard composer behavior; let the parent handle Enter.
    if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
      e.preventDefault()
      onSubmit()
    }
  }

  return (
    <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        inputRef={ref}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        placeholder={placeholder}
        size="small" fullWidth
        multiline={multiline}
        maxRows={maxRows}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: 14, bgcolor: 'transparent',
            '& fieldset': { border: 'none' },
          },
          '& .MuiOutlinedInput-input': { py: 1 },
          ...sx,
        }}
      />
      <Popper
        open={!!picker && (results.length > 0 || searchQ.isLoading)}
        anchorEl={anchorRef.current}
        placement="top-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <ClickAwayListener onClickAway={() => setPicker(null)}>
          <Paper elevation={6} sx={{
            width: 320, maxHeight: 280, overflow: 'auto',
            borderRadius: 2, border: '1px solid', borderColor: 'divider',
          }}>
            {searchQ.isLoading ? (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={18} />
              </Box>
            ) : results.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', p: 2 }}>
                No matches
              </Typography>
            ) : (
              results.map((r, i) => {
                const meta = TYPE_META[r.type]
                const Icon = meta.icon
                const isActive = i === activeIdx
                return (
                  <Stack key={`${r.type}:${r.id}`} direction="row" alignItems="center" spacing={1.25}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => pickResult(r)}
                    sx={{
                      cursor: 'pointer', px: 1.5, py: 1,
                      bgcolor: isActive ? (t) => t.palette.mode === 'light'
                        ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.18)' : 'transparent',
                      '&:hover': { bgcolor: (t) => t.palette.mode === 'light'
                        ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.18)' },
                    }}>
                    {r.image ? (
                      <Avatar src={r.image} sx={{ width: 28, height: 28 }} />
                    ) : (
                      <Box sx={{
                        width: 28, height: 28, borderRadius: 1.5,
                        bgcolor: `${meta.color}1F`, color: meta.color,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon sx={{ fontSize: 16 }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
                        {r.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                        {meta.label}{r.sublabel ? ` · ${r.sublabel}` : ''}
                      </Typography>
                    </Box>
                  </Stack>
                )
              })
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Bubble renderer
// ────────────────────────────────────────────────────────────────────────────

interface MentionTextProps {
  text: string
  mentions: ChatMention[]
  onTap?: (mention: ChatMention) => void
  sx?: any
  /** Active conversation theme — `isMe` flips chip colors so they read on
   *  the primary-colored "my message" bubble background. */
  inverse?: boolean
}

/** Renders message text with `@user` / `#entity` chips inline. */
export function MentionText({ text, mentions, onTap, sx, inverse }: MentionTextProps) {
  const segments = useMemo(() => splitTextWithMentions(text, mentions), [text, mentions])
  return (
    <Typography component="span" sx={{ fontSize: 13.5, lineHeight: 1.55, ...sx }}>
      {segments.map((s, i) =>
        s.kind === 'text'
          ? <span key={i}>{s.value}</span>
          : <MentionChip key={i} mention={s.mention} onTap={onTap} inverse={inverse} />
      )}
    </Typography>
  )
}

function MentionChip({ mention, onTap, inverse }: {
  mention: ChatMention
  onTap?: (mention: ChatMention) => void
  inverse?: boolean
}) {
  const meta = TYPE_META[mention.type]
  const prefix = mention.type === 'user' ? '@' : '#'
  // Solid color pill — same per-type color regardless of which bubble
  // background it sits on. White text + saturated bg means the type
  // signal survives the "my message" primary-color bubble where
  // tinted backgrounds would otherwise wash out.
  return (
    <Box
      component="span"
      onClick={(e) => { e.stopPropagation(); onTap?.(mention) }}
      sx={{
        display: 'inline',
        px: 0.85, py: 0.1, mx: '1.5px',
        borderRadius: 1,
        cursor: onTap ? 'pointer' : 'default',
        fontWeight: 700,
        fontSize: 'inherit',
        color: '#fff',
        bgcolor: meta.color,
        // On the inverse (own-message) bubble we keep the per-type color
        // but soften the surrounding contrast with a thin white ring so
        // it doesn't fight the primary-color bubble at full saturation.
        boxShadow: inverse
          ? '0 0 0 1.5px rgba(255,255,255,0.55)'
          : 'none',
        '&:hover': onTap ? {
          filter: 'brightness(1.08)',
        } : undefined,
      }}
    >
      {prefix}{mention.label}
    </Box>
  )
}

type Segment =
  | { kind: 'text'; value: string }
  | { kind: 'mention'; mention: ChatMention }

/**
 * Walks the text and slices out the longest mention-label match at each
 * position. The mentions array carries every `{type, id, label}` we
 * inserted via the picker — we do a label-prefix search rather than a
 * regex so spaces and punctuation in labels are handled correctly.
 */
function splitTextWithMentions(text: string, mentions: ChatMention[]): Segment[] {
  if (!mentions.length || !text) return [{ kind: 'text', value: text }]
  // Sort longest label first so "Alice Smith" wins over "Alice".
  const sorted = [...mentions].sort((a, b) => b.label.length - a.label.length)
  const segments: Segment[] = []
  let i = 0
  let buf = ''
  while (i < text.length) {
    const ch = text[i]
    if (ch === '@' || ch === '#') {
      const expectType = ch === '@' ? 'user' : null // `null` = any non-user
      let matched: ChatMention | null = null
      for (const m of sorted) {
        const sameType = expectType === 'user'
          ? m.type === 'user'
          : m.type !== 'user'
        if (!sameType) continue
        if (text.startsWith(m.label, i + 1)) { matched = m; break }
      }
      if (matched) {
        if (buf) { segments.push({ kind: 'text', value: buf }); buf = '' }
        segments.push({ kind: 'mention', mention: matched })
        i += 1 + matched.label.length
        continue
      }
    }
    buf += ch
    i++
  }
  if (buf) segments.push({ kind: 'text', value: buf })
  return segments
}
